import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../store';
import { setCredentials } from '../store/authSlice';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ShieldAlert, Cpu } from 'lucide-react';
import { apiPost } from '../utils/api';
import { getEnv } from '../utils/env';
import { trackGAEvent, setGAUserId } from '../utils/analytics';

export const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Load Google Identity Services SDK dynamically
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    sessionStorage.clear();
    const clientId = getEnv('VITE_GOOGLE_CLIENT_ID', 'mockclientid');

    // Check if script is already loaded
    let script = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement;

    const initializeGoogle = () => {
      if ((window as any).google?.accounts?.id) {
        (window as any).google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
        });

        (window as any).google.accounts.id.renderButton(document.getElementById('google-signin-button'), {
          theme: 'filled_blue',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: 320,
        });

        // Optional One-Tap prompt
        (window as any).google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('One Tap prompt dismissed or not displayed');
          }
        });
      }
    };

    if (!script) {
      script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    } else {
      initializeGoogle();
    }
  }, []);

  const decodeJwt = (token: string): any => {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch {
      return null;
    }
  };

  const handleGoogleCredentialResponse = async (response: any) => {
    setLoading(true);
    setError(null);

    try {
      const idToken = response.credential;
      const data = await apiPost('/api/auth/google', { idToken });

      const decoded = decodeJwt(data.accessToken);
      if (decoded && decoded.sub) {
        setGAUserId(decoded.sub);
      }

      if (data.isNewUser) {
        trackGAEvent('sign_up', { method: 'google' });
      } else {
        trackGAEvent('login', { method: 'google' });
      }

      dispatch(setCredentials({ token: data.accessToken }));
      navigate('/library');
    } catch (err: any) {
      setError(err.message || 'Google authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Developer Bypass Login handler (Mock exchange for local development)
  const handleDevBypass = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiPost('/api/auth/google', { idToken: 'mock-dev-token' });

      const decoded = decodeJwt(data.accessToken);
      if (decoded && decoded.sub) {
        setGAUserId(decoded.sub);
      }

      if (data.isNewUser) {
        trackGAEvent('sign_up', { method: 'dev_bypass' });
      } else {
        trackGAEvent('login', { method: 'dev_bypass' });
      }

      dispatch(setCredentials({ token: data.accessToken }));
      navigate('/library');
    } catch (err: any) {
      setError(err.message || 'Developer bypass login failed');
    } finally {
      setLoading(false);
    }
  };

  const isProduction = getEnv('NODE_ENV') === 'production' || import.meta.env.MODE === 'production';



  return (
    <div
      className="flex-center"
      style={{
        minHeight: '100vh',
        padding: '24px',
        background: 'radial-gradient(circle at top left, #1a1525 0%, #0d0b13 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated glowing mesh details */}
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          top: '-150px',
          left: '-150px',
          background: 'radial-gradient(circle, rgba(255, 56, 92, 0.08) 0%, rgba(255, 56, 92, 0) 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          bottom: '-150px',
          right: '-150px',
          background: 'radial-gradient(circle, rgba(255, 122, 0, 0.06) 0%, rgba(255, 122, 0, 0) 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />

      <div
        className="glass-panel"
        style={{
          width: '100%',
          maxWidth: '420px',
          padding: '40px',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.04)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          background: 'rgba(21, 18, 30, 0.7)',
          backdropFilter: 'blur(20px)',
          zIndex: 1,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2
            style={{
              fontSize: '32px',
              fontWeight: 800,
              marginBottom: '8px',
              background: 'linear-gradient(135deg, #fff 0%, #b3b1b9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block',
              letterSpacing: '-0.02em',
            }}
          >
            Trail Render
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>GPS Telemetry to Cinematic 3D Flyovers</p>
        </div>

        {error && (
          <div
            className="flex-center"
            style={{
              gap: '10px',
              padding: '14px',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.15)',
              borderRadius: '12px',
              color: '#f87171',
              marginBottom: '32px',
              fontSize: '14px',
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>{error}</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
          {/* Main Google Sign-In Container */}
          <div
            id="google-signin-button"
            style={{
              minHeight: '44px',
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              transition: 'opacity 0.2s',
            }}
            className={loading ? 'loading-opaque' : ''}
          />

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
              <div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Signing in...</span>
            </div>
          )}
        </div>

        {/* Local Developer Bypass login */}
        {!isProduction && (
          <div
            style={{
              marginTop: '48px',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              paddingTop: '24px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-muted)',
                fontSize: '12px',
                marginBottom: '12px',
              }}
            >
              <ShieldAlert size={14} />
              <span>Local Development Environment</span>
            </div>

            <button
              type="button"
              onClick={handleDevBypass}
              disabled={loading}
              className="btn"
              style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                width: '100%',
                padding: '10px 16px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
              }}
            >
              <Cpu size={15} />
              <span>Developer Bypass Sign-In</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
